import React, { useState, useEffect } from 'react';
import { Config } from 'config/envConfig';

import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { CollapsedSideBar } from '../collapsedSideBar';
import {
    faGear,
    faGears,
    faUser,
    faBuilding,
    faUserTie,
    faFileLines,
    faScrewdriverWrench,
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../navbar';
import { SettingsContainer } from './settingsContainer';
import { SettingsSideBar } from './settingsSidebar';
import TransactionList from '../Transactionlist';
import { useGlobalCredits } from '../../context/GlobalStateContext';
import { Features, SettingsFeatures, isFeatureEnabled } from '@quyva/common';
import { useFeatureFlags } from '../../context';
import { ConfirmModal } from '@quyva/shared-ui';
import { useNavigate } from 'react-router-dom';
import { SettingsProvider, useSettingsContext } from '../../context';

export const SettingSections = Object.freeze({
    ACCOUNT: 'Account Settings',
    GENERAL: 'General Settings',
    CHATS: 'Chat Settings',
    ORGANIZATION: 'Organization Settings',
    ORGANIZATION_MANAGEMENT: 'Organization Management',
    PROMPT_SETTINGS: 'Prompt Settings',
    SYSTEM_SETTINGS: 'System Settings',
    MAINTENANCE: 'Maintenance',
});

const formatProcessName = (name) => {
    if (!name) return '';
    return name
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const SettingsView = ({
    newNotification,
    setNewNotification,
    viewNotifications,
    setViewNotifications,
    messages,
    socket,
    isSideViewExpanded,
    setIsSideViewExpanded,
    planName,
}) => {
    const { isAdmin } = useGlobalCredits();

    return (
        <SettingsProvider isAdmin={isAdmin}>
            <SettingsViewInner
                newNotification={newNotification}
                setNewNotification={setNewNotification}
                viewNotifications={viewNotifications}
                setViewNotifications={setViewNotifications}
                messages={messages}
                socket={socket}
                isSideViewExpanded={isSideViewExpanded}
                setIsSideViewExpanded={setIsSideViewExpanded}
                planName={planName}
            />
        </SettingsProvider>
    );
};

const SettingsViewInner = ({
    newNotification,
    setNewNotification,
    viewNotifications,
    setViewNotifications,
    messages,
    socket,
    isSideViewExpanded,
    setIsSideViewExpanded,
    planName,
}) => {
    const { featureFlagsData } = useFeatureFlags();
    const navigate = useNavigate();
    const accountSettingsRef = React.createRef();
    const generalSettingsRef = React.createRef();
    // const chatsSettingsRef = React.createRef();
    const organizationSettingsRef = React.createRef();
    const organizationManagementRef = React.createRef();
    const promptSettingsRef = React.createRef();
    const systemSettingsRef = React.createRef();
    const maintenanceRef = React.createRef();
    const [isMobile, setIsMobile] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const [pendingSection, setPendingSection] = useState(null);
    const [pendingRoute, setPendingRoute] = useState(null);
    const [pendingProcess, setPendingProcess] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const { transactionMode, isAdmin } = useGlobalCredits();
    const {
        processes,
        selectedProcess,
        setSelectedProcess,
        handleInstructionChange,
        handlePromptNameChange,
        hasAnyUnsavedChanges,
        discardAllChanges,
        isSuperAdmin,
        isUserKey,
    } = useSettingsContext();
    const settingSectionsData = [];

    if (
        isFeatureEnabled(
            [Features.SETTINGS, SettingsFeatures.ACCOUNT_SETTINGS],
            featureFlagsData
        )
    ) {
        settingSectionsData.push({
            name: SettingSections.ACCOUNT,
            icon: faUser,
            hoverText: 'Account settings',
            ref: accountSettingsRef,
        });
    }
    if (
        isFeatureEnabled(
            [Features.SETTINGS, SettingsFeatures.GENERAL_SETTINGS],
            featureFlagsData
        )
    ) {
        settingSectionsData.push({
            name: SettingSections.GENERAL,
            icon: faGears,
            hoverText: 'Global basic settings',
            ref: generalSettingsRef,
        });
    }
    if (
        isFeatureEnabled(
            [Features.SETTINGS, SettingsFeatures.ORGANIZATION_SETTINGS],
            featureFlagsData
        )
    ) {
        settingSectionsData.push({
            name: SettingSections.ORGANIZATION,
            icon: faBuilding,
            hoverText: 'Organization settings',
            ref: organizationSettingsRef,
        });
    }
    if (isSuperAdmin &&
        isFeatureEnabled(
            [Features.SETTINGS, SettingsFeatures.ORGANIZATION_MANAGEMENT],
            featureFlagsData
        )
    ) {
        if (isSuperAdmin) {
            settingSectionsData.push({
                name: SettingSections.ORGANIZATION_MANAGEMENT,
                icon: faUserTie,
                hoverText: 'Organization management',
                ref: organizationManagementRef,
            });
        }
    }
    // if (
    //     isFeatureEnabled(
    //         [Features.SETTINGS, SettingsFeatures.CHAT_SETTINGS],
    //         featureFlagsData
    //     )
    // ) {
    //     settingSectionsData.push({
    //         name: SettingSections.CHATS,
    //         icon: faComments,
    //         hoverText: 'Global Chat settings',
    //         ref: chatsSettingsRef,
    //     });
    // }
    if (
        isFeatureEnabled(
            [Features.SETTINGS, SettingsFeatures.PROMPT_SETTINGS],
            featureFlagsData
        )
    ) {
        if (isAdmin) {
            settingSectionsData.push({
                name: SettingSections.PROMPT_SETTINGS,
                icon: faFileLines,
                hoverText: 'Prompt Settings',
                ref: promptSettingsRef,
            });
        }
    }

    if (isSuperAdmin && isUserKey &&
        isFeatureEnabled(
            [Features.SETTINGS, SettingsFeatures.SYSTEM_SETTINGS],
            featureFlagsData
        )
    ) {
        settingSectionsData.push({
            name: SettingSections.SYSTEM_SETTINGS,
            icon: faGear,
            hoverText: 'System level configuration',
            ref: systemSettingsRef,
        });
    }

    if (isSuperAdmin) {
        settingSectionsData.push({
            name: SettingSections.MAINTENANCE,
            icon: faScrewdriverWrench,
            hoverText: 'Schedule and manage server maintenance windows',
            ref: maintenanceRef,
        });
    }



    // Responsive check
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleProcessSelect = (processName) => {
        if (hasAnyUnsavedChanges) {
            setPendingProcess(processName);
            setIsConfirmOpen(true);
            return;
        }
        setSelectedProcess(processName);
    };

    const handleSectionSelect = (sectionName, ref) => {
        if (hasAnyUnsavedChanges) {
            setPendingSection(sectionName);
            setIsConfirmOpen(true);
            return;
        }
        setActiveSection(sectionName);
    };

    useEffect(() => {
        if (activeSection) return;
        if (settingSectionsData.length === 0) return;
        setActiveSection(settingSectionsData[0].name);
    }, [activeSection, settingSectionsData.length]);

    // Intercept route navigations while on Settings if there are unsaved changes.
    useEffect(() => {
        const originalNavigate = window.__quyvaNavigate;
        // If other pages set a global navigate hook, don't clobber it.
        if (!window.__quyvaNavigate) {
            window.__quyvaNavigate = (to) => navigate(to);
        }

        // Patch global navigate hook to allow interception.
        const prev = window.__quyvaNavigate;
        window.__quyvaNavigate = (to) => {
            if (hasAnyUnsavedChanges) {
                setPendingRoute(to);
                setIsConfirmOpen(true);
                return;
            }
            navigate(to);
        };

        return () => {
            // restore
            window.__quyvaNavigate = prev || originalNavigate;
        };
    }, [navigate, hasAnyUnsavedChanges]);

    return (
        <div className='z-0 w-[99vw] h-[99vh] m-auto pt-2'>
            <ReflexContainer orientation='vertical'>
                {!isMobile && (
                    <ReflexElement
                        className='left-pane mr-0.5'
                        minSize={isSideViewExpanded ? 250 : 50}
                        size={isSideViewExpanded ? 350 : 50}
                        maxSize={isSideViewExpanded ? 700 : 50}
                    >
                        <div className='pane-content w-full h-full bg-transparent'>
                            {isSideViewExpanded ? (
                                <div className='w-full h-full border border-2 overflow-auto bg-backgrounds-slate rounded-xl p-2 z-10'>
                                    <SettingsSideBar
                                        settingSections={settingSectionsData}
                                        activeSection={activeSection}
                                        setActiveSection={handleSectionSelect}
                                        setIsLeftViewExpanded={
                                            setIsSideViewExpanded
                                        }
                                        planName={planName}
                                        processes={processes}
                                        selectedProcess={selectedProcess}
                                        setSelectedProcess={handleProcessSelect}
                                        formatProcessName={formatProcessName}
                                    />
                                </div>
                            ) : (
                                <CollapsedSideBar
                                    icon={faGear}
                                    iconColor={'icons-primary'}
                                    title={'Setting Sections'}
                                    setIsRepoViewExpanded={
                                        setIsSideViewExpanded
                                    }
                                    planName={planName}
                                />
                            )}
                        </div>
                    </ReflexElement>
                )}
                <ReflexSplitter
                    style={{
                        border: 0,
                        background: 'white',
                        width: '6px',
                        zIndex: 10,
                        height: '98vh',
                    }}
                />

                <ReflexElement className='right-pane ml-0.5'>
                    <div className='pane-content w-full h-full bg-transparent'>
                        <div className='w-full h-full border border-2 overflow-auto bg-backgrounds-slate rounded-xl z-0'>
                            <ReflexContainer orientation='horizontal'>
                                <ReflexElement
                                    className='header'
                                    style={{
                                        minHeight: '4rem',
                                        height: '4rem',
                                        maxHeight: '4rem',
                                    }}
                                >
                                    <div className='pane-content w-full h-full p-2'>
                                        <Navbar
                                            settingSections={
                                                settingSectionsData
                                            }
                                            activeSection={activeSection}
                                            setActiveSection={
                                                handleSectionSelect
                                            }
                                            setIsLeftViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                            //Navbar needs to be modified
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                        />
                                    </div>
                                </ReflexElement>

                                <ReflexElement flex={1}>
                                    <div className='pane-content h-full w-full p-2'>
                                        {transactionMode ? (
                                            <>
                                                <TransactionList />
                                            </>
                                        ) : (
                                            <SettingsContainer
                                                accountSettingsRef={
                                                    accountSettingsRef
                                                }
                                                organizationManagementRef={
                                                    organizationManagementRef
                                                }
                                                generalSettingsRef={
                                                    generalSettingsRef
                                                }
                                                // chatsSettingsRef={
                                                //     chatsSettingsRef
                                                // }
                                                organizationSettingsRef={
                                                    organizationSettingsRef
                                                }
                                                promptSettingsRef={
                                                    promptSettingsRef
                                                }
                                                maintenanceRef={maintenanceRef}
                                                activeSection={activeSection}
                                                setActiveSection={
                                                    setActiveSection
                                                }
                                                isAdmin={isAdmin}
                                            />
                                        )}
                                    </div>
                                </ReflexElement>
                            </ReflexContainer>
                        </div>
                    </div>
                </ReflexElement>
            </ReflexContainer>

            {isConfirmOpen && (
                <ConfirmModal
                    modalText={
                        "Your changes won't save if you switch. Do you want to continue?"
                    }
                    onConfirm={() => {
                        discardAllChanges();
                        if (pendingProcess) {
                            setSelectedProcess(pendingProcess);
                        }
                        if (pendingSection) {
                            setActiveSection(pendingSection);
                        }
                        if (pendingRoute) {
                            navigate(pendingRoute);
                        }
                        setPendingProcess(null);
                        setPendingSection(null);
                        setPendingRoute(null);
                        setIsConfirmOpen(false);
                    }}
                    onCancel={() => {
                        setPendingProcess(null);
                        setPendingSection(null);
                        setPendingRoute(null);
                        setIsConfirmOpen(false);
                    }}
                    confirmText='Discard Changes'
                    cancelText='Stay'
                />
            )}
        </div>
    );
};
